import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { buscarDireccion } from '@/lib/geocodificacion/google'
import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type { CandidatoDelDia } from '@/lib/supabase/tipos-bd'

/**
 * Estación 3 · Programación.
 *
 * Se asignan las tres cosas juntas —quién, cuántas horas y cuándo— porque sin
 * las tres no se puede medir nada después. La base lo exige desde 0007; aquí
 * solo se pide bien.
 */

/** A quién le queda de paso ese día, ordenado por lo que conviene mirar primero. */
export function useCandidatos(ordenServicioId: string | null, fecha: string | null) {
  const consulta = useQuery({
    queryKey: ['quien-puede', ordenServicioId, fecha],
    enabled: Boolean(ordenServicioId && fecha),
    staleTime: 30 * 1000,
    queryFn: async (): Promise<CandidatoDelDia[]> => {
      const { data, error } = await supabase.rpc('fn_quien_puede_ese_dia', {
        p_orden_servicio_id: ordenServicioId!,
        p_fecha: fecha!,
      })

      if (error) throw new Error(mensajeDeError(error))

      return ((data ?? []) as unknown as CandidatoDelDia[]).map((fila) => ({
        ...fila,
        horas_ese_dia: Number(fila.horas_ese_dia),
        distancia_km: fila.distancia_km === null ? null : Number(fila.distancia_km),
        cerca_de_latitud: fila.cerca_de_latitud === null ? null : Number(fila.cerca_de_latitud),
        cerca_de_longitud: fila.cerca_de_longitud === null ? null : Number(fila.cerca_de_longitud),
      }))
    },
  })

  return { candidatos: consulta.data ?? [], cargando: consulta.isLoading }
}

export type Programacion = {
  ordenTrabajoId: string
  colaboradorId: string
  horas: number
  inicio: string
  fin: string | null
}

export function useProgramar() {
  const clienteConsultas = useQueryClient()

  return useMutation({
    mutationFn: async (datos: Programacion) => {
      const { error } = await supabase.rpc('fn_programar_orden_trabajo', {
        p_orden_trabajo_id: datos.ordenTrabajoId,
        p_colaborador_id: datos.colaboradorId,
        p_horas: datos.horas,
        p_inicio: datos.inicio,
        p_fin: datos.fin,
      })

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: async () => {
      await Promise.all(
        [
          ['ordenes-servicio'],
          ['orden-servicio'],
          ['cronograma'],
          ['lo-que-espera'],
          ['quien-puede'],
          ['trazabilidad'],
        ].map((clave) => clienteConsultas.invalidateQueries({ queryKey: clave })),
      )
    },
  })
}

/** Reasignar el técnico sin tocar fechas ni horas (migración 0149). */
export function useReasignarTecnico() {
  const clienteConsultas = useQueryClient()

  return useMutation({
    mutationFn: async ({
      ordenTrabajoId,
      colaboradorId,
    }: {
      ordenTrabajoId: string
      colaboradorId: string
    }) => {
      const { error } = await supabase.rpc('fn_reasignar_tecnico', {
        p_orden_trabajo_id: ordenTrabajoId,
        p_colaborador_id: colaboradorId,
      })
      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: async () => {
      await Promise.all(
        [['ordenes-servicio'], ['orden-servicio'], ['cronograma'], ['trazabilidad']].map((clave) =>
          clienteConsultas.invalidateQueries({ queryKey: clave }),
        ),
      )
    },
  })
}

/**
 * Ubicar ESTA dirección, sin salir de la programación.
 *
 * Una orden sin punto no se puede programar mirando el mapa, y mandar al
 * Gerente a la Sección 5 a ubicarla para volver es hacerle perder el hilo de
 * lo que estaba decidiendo.
 */
export function useUbicarEsta() {
  const clienteConsultas = useQueryClient()

  return useMutation({
    // La vista trae ciudad y departamento con coalesce a Cartago y Valle del
    // Cauca, así que aquí nunca llegan nulos: pedirlos nulos obligaría a
    // inventar un respaldo que ya existe en la base.
    mutationFn: async (orden: {
      id: string
      direccion: string
      ciudad: string
      departamento: string
    }) => {
      const punto = await buscarDireccion({
        direccion: orden.direccion,
        ciudad: orden.ciudad,
        departamento: orden.departamento,
      })

      const { error } = await supabase.rpc('fn_ubicar_orden', {
        p_orden_id: orden.id,
        p_latitud: punto?.latitud ?? null,
        p_longitud: punto?.longitud ?? null,
        p_origen: 'geocodificada',
        p_precision: punto?.precision ?? null,
        p_texto: punto?.texto ?? null,
      })

      if (error) throw new Error(mensajeDeError(error))

      return punto
    },
    onSuccess: async () => {
      await Promise.all(
        [['ordenes-servicio'], ['orden-servicio'], ['quien-puede'], ['ordenes-ubicadas']].map(
          (clave) => clienteConsultas.invalidateQueries({ queryKey: clave }),
        ),
      )
    },
  })
}
